import { push as redirectTo } from 'connected-react-router';
import { Formik } from 'formik';
import React from 'react';
import { useDispatch, connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import {
  AirBalloon,
  FlexCenter,
  FlexColumn as RegularFlexColumn,
  Icon,
  Header,
  COLORS,
} from '@summer/ui-components';

import VerifyEmailForm from 'components/pages/onboarding/VerifyEmailForm';
import { CreateAccountHeader } from 'components/pages/userIdentityVerification/CreateAccountHeader';
import { Link } from 'components/shared/typography';
import { TabletSize } from 'constants/styleguide';
import { clearServerError } from 'redux/actions/ui.actions';
import { sendVerificationEmailCode } from 'redux/actions/user.actions';
import { isLoading, getErrorCode } from 'redux/selectors/ui.selectors';
import { verifyEmailValidationSchema } from 'schemas/onboard';

const errorMessages = new Map([
  [409, 'This email is already associated with an account.'],
  [429, 'Too many requests. Please wait for 1 minute before trying again.'],
  [500, 'Something went wrong. Please try again.'],
]);

const getErrorMessage = (errorCode, dispatch) => {
  switch (errorCode) {
    case 409:
      return (
        <>
          {errorMessages.get(errorCode)}{' '}
          <Link
            onClick={() => dispatch(redirectTo('/login'))}
            inheritColor
            underline
          >
            Sign in
          </Link>
        </>
      );
    case 429:
    case 500:
      return errorMessages.get(errorCode);
    default:
      return 'Something went wrong. Please try again.';
  }
};

const VerifyEmail = ({ isLoading, errorCode }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const subHeaderText = 'Eligibility Verified!';
  const subHeaderSecondaryText = 'Hooray! Now create your account';

  const handleSubmit = values => {
    const { email, firstName, lastName } = values;
    dispatch(
      sendVerificationEmailCode({
        email,
        meta: { email, firstName, lastName },
      }),
    );
  };

  const handleEmailChange = () => {
    dispatch(clearServerError('sendEmailVerificationCode'));
  };

  const renderForm = formik => {
    const {
      values,
      errors,
      touched,
      handleChange,
      handleBlur,
      isSubmitting,
      isValid,
    } = formik;
    const isButtonDisabled = !isValid;
    const onButtonClick = () => {
      formik.handleSubmit();
    };
    return (
      <VerifyEmailForm
        values={values}
        errors={errors}
        touched={touched}
        handleChange={handleChange}
        handleBlur={handleBlur}
        isSubmitting={isSubmitting}
        isButtonDisabled={isButtonDisabled}
        isLoading={isLoading}
        onButtonClick={onButtonClick}
        errorMessage={errorCode ? getErrorMessage(errorCode, dispatch) : null}
        onEmailChange={handleEmailChange}
      />
    );
  };
  return (
    <FlexColumn>
      <HeaderSection>
        <CreateAccountHeader
          title="Create Account"
          currentStep={1}
          totalSteps={3}
        />
        <AirBalloonIcon SvgComponent={AirBalloon} stroke={COLORS.azure} />
        <Header center>{subHeaderText}</Header>
        {subHeaderSecondaryText && (
          <Header as="h3" color="secondary" center>
            {subHeaderSecondaryText}
          </Header>
        )}
      </HeaderSection>
      <ContentSection>
        <Formik
          initialValues={{
            firstName: location?.state?.firstName ?? '',
            lastName: location?.state?.lastName ?? '',
            email: '',
          }}
          validationSchema={verifyEmailValidationSchema}
          onSubmit={handleSubmit}
        >
          {renderForm}
        </Formik>
      </ContentSection>
    </FlexColumn>
  );
};

const FlexColumn = styled(RegularFlexColumn)`
  flex: 0 1 auto;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: ${TabletSize}) {
    margin-bottom: 24px;
  }
`;

const HeaderSection = styled(FlexCenter)`
  flex: 0 1 auto;
  width: 100%;
  max-width: 600px;
  padding: 0 24px;
  align-items: unset;
  margin-bottom: 40px;
  max-height: max-content;
  flex-direction: column;
`;

const ContentSection = styled(FlexCenter)`
  flex: 0 1 auto;
  width: 100%;
  max-width: 600px;
  align-self: center;
  flex-direction: column;
  padding: 0;
`;

const AirBalloonIcon = styled(Icon)`
  margin-bottom: 16px;
  width: 34px;
  height: 49px;
  align-self: center;
`;

const mapStateToProps = state => ({
  isLoading: isLoading(state, 'sendEmailVerificationCode'),
  errorCode: getErrorCode(state, 'sendEmailVerificationCode'),
});

export default connect(mapStateToProps)(VerifyEmail);
